import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Search } from 'lucide-react';
import api from '../../services/api';

const TYPE_COLORS = {
  asset:     'bg-blue-50 text-blue-700',
  liability: 'bg-red-50 text-red-700',
  equity:    'bg-purple-50 text-purple-700',
  revenue:   'bg-emerald-50 text-emerald-700',
  cogs:      'bg-orange-50 text-orange-700',
  expense:   'bg-slate-100 text-slate-600',
};
const TYPES = ['asset','liability','equity','revenue','cogs','expense'];
const inputCls = 'w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-900';

export default function ChartOfAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ account_code: '', account_name: '', account_type: 'expense', account_subtype: 'other', entity: 'Both', currency: 'USD' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (typeFilter) params.set('account_type', typeFilter);
    api.get(`/api/accounts/chart?${params}`)
      .then(r => setAccounts(r.data))
      .finally(() => setLoading(false));
  }, [search, typeFilter]);

  useEffect(() => { load(); }, [load]);

  const grouped = TYPES.reduce((acc, t) => {
    acc[t] = accounts.filter(a => a.account_type === t);
    return acc;
  }, {});

  const save = async () => {
    if (!form.account_code || !form.account_name) return;
    setSaving(true);
    try {
      await api.post('/api/accounts/chart', form);
      setShowAdd(false);
      setForm({ account_code: '', account_name: '', account_type: 'expense', account_subtype: 'other', entity: 'Both', currency: 'USD' });
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  const toggle = async (account_id, is_active) => {
    await api.put(`/api/accounts/chart/${account_id}`, { is_active: !is_active });
    load();
  };

  return (
    <div className="p-6 fade-in" data-testid="chart-of-accounts-page">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Chart of Accounts</h1>
        <div className="flex gap-2">
          <button onClick={() => setShowAdd(p => !p)}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-indigo-900 text-white text-sm font-medium rounded-md hover:bg-indigo-800"
            data-testid="add-account-btn">
            <Plus size={14} /> Add Account
          </button>
        </div>
      </div>

      {showAdd && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div><label className="text-xs text-slate-500 mb-1 block">Code*</label>
            <input value={form.account_code} onChange={e => setForm(p => ({ ...p, account_code: e.target.value }))} className={inputCls} /></div>
          <div className="col-span-2"><label className="text-xs text-slate-500 mb-1 block">Name*</label>
            <input value={form.account_name} onChange={e => setForm(p => ({ ...p, account_name: e.target.value }))} className={inputCls} /></div>
          <div><label className="text-xs text-slate-500 mb-1 block">Type</label>
            <select value={form.account_type} onChange={e => setForm(p => ({ ...p, account_type: e.target.value }))} className={inputCls}>
              {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="flex gap-2 col-span-4">
            <button onClick={save} disabled={saving} className="px-4 py-2 bg-indigo-900 text-white text-xs rounded hover:bg-indigo-800 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save Account'}
            </button>
            <button onClick={() => setShowAdd(false)} className="text-xs text-slate-500 px-2">Cancel</button>
          </div>
        </div>
      )}

      <div className="flex gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input placeholder="Search accounts…" value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-900" />
        </div>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
          className="border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none">
          <option value="">All Types</option>
          {TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="space-y-2">{[...Array(5)].map((_, i) => <div key={i} className="h-10 bg-slate-100 rounded animate-pulse" />)}</div>
      ) : (
        <div className="space-y-4">
          {TYPES.filter(t => !typeFilter || t === typeFilter).map(type => (
            grouped[type].length > 0 ? (
              <div key={type} className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`coa-section-${type}`}>
                <div className="flex items-center gap-2 px-4 py-3 bg-slate-50 border-b border-slate-200">
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold uppercase ${TYPE_COLORS[type]}`}>{type}</span>
                  <span className="text-xs text-slate-400">{grouped[type].length} accounts</span>
                </div>
                <table className="w-full text-sm">
                  <tbody className="divide-y divide-slate-50">
                    {grouped[type].map(acc => (
                      <tr key={acc.account_id} className={`hover:bg-slate-50/50 ${!acc.is_active ? 'opacity-40' : ''}`}>
                        <td className="px-4 py-2.5 w-20 font-mono text-xs text-slate-500">{acc.account_code}</td>
                        <td className="px-4 py-2.5 text-xs font-medium text-slate-800">{acc.account_name}</td>
                        <td className="px-4 py-2.5 text-xs text-slate-400">{acc.account_subtype}</td>
                        <td className="px-4 py-2.5 text-right">
                          <button onClick={() => toggle(acc.account_id, acc.is_active)}
                            className={`text-xs px-2 py-0.5 rounded hover:opacity-80 ${acc.is_active ? 'text-slate-400 hover:text-red-500' : 'text-emerald-600'}`}>
                            {acc.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null
          ))}
        </div>
      )}
    </div>
  );
}
